import BrandLogo from '../Brand/BrandLogo';
import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Avatar, Menu, MenuItem, Chip, Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon, SpaceDashboard as OverviewIcon, Business as CustomersIcon, Tune as SystemIcon,
  FactCheck as AuditIcon, SupportAgent as SupportIcon, ArrowBack as BackIcon, Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 248;
const DRAWER_BG = '#0b1220';
const APPBAR_BG = '#111a2e';
const ACCENT = '#f59e0b';

const navItems = [
  { text: 'Overview', icon: <OverviewIcon />, path: '/admin', exact: true },
  { text: 'Customers', icon: <CustomersIcon />, path: '/admin/customers' },
  { text: 'Support', icon: <SupportIcon />, path: '/admin/support' },
  { text: 'System settings', icon: <SystemIcon />, path: '/admin/system-settings' },
  { text: 'Audit', icon: <AuditIcon />, path: '/admin/audit' },
];

const AdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSelected = (item) => (item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path));

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const initials = user ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() || (user.email || '?')[0].toUpperCase() : '?';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: DRAWER_BG, color: 'grey.300' }}>
      <Toolbar sx={{ gap: 1.5 }}>
        <Box>
          <BrandLogo height={30} onDark />
          <Typography variant="caption" sx={{ color: ACCENT, letterSpacing: 1, textTransform: 'uppercase', display: 'block' }}>Platform admin</Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List sx={{ flexGrow: 1, px: 1, pt: 1 }}>
        {navItems.map((item) => {
          const selected = isSelected(item);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selected}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 1.5,
                  color: selected ? '#fff' : 'grey.400',
                  '&.Mui-selected': { bgcolor: 'rgba(245,158,11,0.16)', borderLeft: `3px solid ${ACCENT}` },
                  '&.Mui-selected:hover': { bgcolor: 'rgba(245,158,11,0.24)' },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemIcon sx={{ color: selected ? ACCENT : 'grey.500', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: selected ? 600 : 400 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List sx={{ px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/dashboard')} sx={{ borderRadius: 1.5, color: 'grey.400', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}>
            <ListItemIcon sx={{ color: 'grey.500', minWidth: 40 }}><BackIcon /></ListItemIcon>
            <ListItemText primary="Back to app" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: APPBAR_BG, borderBottom: `2px solid ${ACCENT}` }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>Platform Admin</Typography>
          <Chip label="STAFF ONLY" size="small" sx={{ ml: 2, bgcolor: ACCENT, color: '#111', fontWeight: 700, display: { xs: 'none', sm: 'inline-flex' } }} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" sx={{ mr: 1.5, display: { xs: 'none', sm: 'block' }, color: 'grey.300' }}>{user?.email}</Typography>
          <Tooltip title="Account">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar sx={{ width: 32, height: 32, bgcolor: ACCENT, color: '#111', fontSize: 14, fontWeight: 700 }}>{initials}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : ''}</Typography>
              <Typography variant="caption" color="text.secondary">Platform administrator</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard'); }}>
              <ListItemIcon><BackIcon fontSize="small" /></ListItemIcon>Back to app
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 0 } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent" open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 0 } }}>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` }, minWidth: 0 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
